import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { apiClient } from 'store/api-client';
import { LocaleEnum } from 'store/cart/types';
import { GetPriceParams, PriceResponse } from 'store/price/types';
import { RootState } from 'store/reducer';
import { useOwnerParams } from 'hooks/use-owner-params.hook';
import moment from 'moment-timezone';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useTimeZone } from 'hooks/use-timezone.hook';
import { searchParams } from 'pages/resource/types';

interface UsePriceQueryParams {
  resourceId: number;
  timezone: string;
  lang?: LocaleEnum;
  enabled?: boolean;
}

export const usePriceQuery = ({ resourceId, timezone, lang, enabled = true }: UsePriceQueryParams) => {
  const state = useSelector((rootState: RootState) => rootState);
  const { ownerType, ownerUuid } = useOwnerParams();
  const { startDate, startTime, endTime } = useSearchParams<searchParams>();
  // Building-tz fallback. A resource rendered from a lighter list/deeplink projection can arrive with
  // an empty `timezone`, and `moment(...).tz('', true)` has no anchor so it falls back to the BROWSER
  // zone — shifting the window (a UTC browser drops the +4h for America/New_York) into the past, which
  // makes helix return empty availability and the price call 500. `||` (not `??`) so '' also falls
  // back. `useTimeZone()` is the app's building-tz hook (selectBuildingTimeZone ?? browser); call it
  // unconditionally here (rules of hooks) and only prefer it when the passed `timezone` is missing.
  const buildingTimeZone = useTimeZone();
  const effectiveTz = timezone || buildingTimeZone;

  const from = endTime ? moment(`${startDate} ${startTime}`).tz(effectiveTz, true)?.utc().format() : null;
  const to = endTime ? moment(`${startDate} ${endTime}`).tz(effectiveTz, true)?.utc().format() : null;

  const isEnabled =
    enabled &&
    Boolean(ownerType) &&
    Boolean(ownerUuid) &&
    Boolean(resourceId) &&
    Boolean(from) &&
    Boolean(to) &&
    Boolean(startDate);

  return useQuery({
    queryKey: ['price', ownerType, ownerUuid, resourceId, from, to, lang],
    queryFn: async (): Promise<PriceResponse> => {
      const client = apiClient(state);
      const params: GetPriceParams = {
        ownerType,
        ownerUuid,
        resourceId,
        from: new Date(from as string),
        to: new Date(to as string),
        lang,
      };

      const response = await firstValueFrom(client.getPrice(params));
      return response.response.data;
    },
    enabled: isEnabled,
    refetchOnMount: true,
  });
};
